import * as THREE from 'three'

export function createFactoryConveyor({ palette, viewDirection }) {
  const root = new THREE.Group()
  const geometries = new Set()
  const materials = new Set()
  const halfLength = 8, radius = .19, beltY = .68, beltTop = beltY + radius
  const beltPerimeter = 4 * halfLength + 2 * Math.PI * radius
  const cargoSpeed = .8, slatCount = 176, cargoCount = 14
  // Half a belt revolution gives an exact same-color slat permutation at reset.
  const routeStart = -beltPerimeter / 4, routeEnd = beltPerimeter / 4
  const duration = (routeEnd - routeStart) / cargoSpeed
  const framingBounds = new THREE.Box3(new THREE.Vector3(-2.85, -.015, -1.4), new THREE.Vector3(2.85, 2.035, 1.4))
  const framingPoints = [
    new THREE.Vector3(-2.85, -.015, 1.4), new THREE.Vector3(2.85, -.015, -1.4),
    new THREE.Vector3(2.85, -.015, 1.4), new THREE.Vector3(-.325, 2.01, -.83),
  ]
  const material = (color, options = {}) => {
    const value = new THREE.MeshBasicMaterial({ color, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1, ...options })
    materials.add(value)
    return value
  }
  const faces = { white: material(palette.white), pale: material(palette.pale), blue: material(palette.blue), side: material(palette.side), gray: material('#526675'), ground: material(palette.ground), green: material('#20ab68') }
  const edge = new THREE.LineBasicMaterial({ color: palette.line })
  materials.add(edge)
  const remember = geometry => { geometries.add(geometry); return geometry }
  function outlined(geometry, surface, parent = root) {
    const group = new THREE.Group()
    const mesh = new THREE.Mesh(remember(geometry), surface)
    group.add(mesh)
    group.add(new THREE.LineSegments(remember(new THREE.EdgesGeometry(geometry, 25)), edge))
    parent.add(group)
    return group
  }
  function box(w, h, d, x, y, z, surface = [faces.white, faces.pale, faces.white, faces.pale, faces.pale, faces.white], parent = root) {
    const group = outlined(new THREE.BoxGeometry(w, h, d), surface, parent)
    group.position.set(x, y, z)
    return group
  }
  const ground = new THREE.Mesh(remember(new THREE.PlaneGeometry(2 * halfLength + 1.7, 2.8)), faces.ground)
  ground.rotation.x = -Math.PI / 2
  ground.position.y = -.015
  root.add(ground)

  // A raised narrow belt with visible return run, end drums and structural legs.
  const supportXs = [-7.75, -1.75, 1.75, 4, 7.75]
  const railEnd = halfLength - .15
  // One extruded silhouette removes internal edges at each beam/post junction.
  const supportProfile = new THREE.Shape()
  supportProfile.moveTo(-railEnd, .35)
  for (const x of supportXs) {
    supportProfile.lineTo(x - .06, .35)
    supportProfile.lineTo(x - .06, .64)
    supportProfile.lineTo(x + .06, .64)
    supportProfile.lineTo(x + .06, .35)
  }
  supportProfile.lineTo(railEnd, .35)
  supportProfile.lineTo(railEnd, .25)
  for (const x of [...supportXs].reverse()) {
    supportProfile.lineTo(x + .06, .25)
    supportProfile.lineTo(x + .06, 0)
    supportProfile.lineTo(x - .06, 0)
    supportProfile.lineTo(x - .06, .25)
  }
  supportProfile.lineTo(-railEnd, .25)
  supportProfile.closePath()
  for (const z of [-.55, .55]) {
    const geometry = new THREE.ExtrudeGeometry(supportProfile, { depth: .12, bevelEnabled: false, steps: 1 })
    geometry.translate(0, 0, -.06)
    const frame = outlined(geometry, [faces.pale, faces.white])
    frame.name = 'conveyor-support-frame'
    frame.position.z = z
    for (const x of supportXs) box(.30, .06, .28, x, .03, z, faces.pale)
  }
  box(2 * halfLength + .45, .14, .12, 0, .68, -.59)
  box(2 * halfLength + .45, .14, .12, 0, .68, .59)
  box(2 * halfLength, .34, 1.02, 0, beltY, 0, faces.gray)
  for (const x of [-halfLength, halfLength]) {
    const drum = outlined(new THREE.CylinderGeometry(radius, radius, 1.02, 32), faces.pale)
    drum.rotation.x = Math.PI / 2
    drum.position.set(x, beltY, 0)
    const axle = outlined(new THREE.CylinderGeometry(.07, .07, 1.30, 16), faces.blue)
    axle.rotation.x = Math.PI / 2
    axle.position.set(x, beltY, 0)
  }
  const slats = Array.from({ length: slatCount }, (_, index) => {
    const slat = box(.105, .024, .99, 0, 0, 0, index % 4 === 0 ? faces.pale : faces.white)
    slat.name = 'belt-slat'
    return slat
  })
  function beltPoint(distance) {
    if (distance < 2 * halfLength) return { x: -halfLength + distance, y: beltTop, angle: 0 }
    distance -= 2 * halfLength
    if (distance < Math.PI * radius) {
      const a = distance / radius
      return { x: halfLength + Math.sin(a) * radius, y: beltY + Math.cos(a) * radius, angle: -a }
    }
    distance -= Math.PI * radius
    if (distance < 2 * halfLength) return { x: halfLength - distance, y: beltY - radius, angle: -Math.PI }
    const a = (distance - 2 * halfLength) / radius
    return { x: -halfLength - Math.sin(a) * radius, y: beltY - Math.cos(a) * radius, angle: -Math.PI - a }
  }

  // The scanner bridges the belt: its open tunnel remains larger than the cargo.
  for (const z of [-.72, .72]) {
    box(.52, 1.26, .22, 0, 1.13, z, [faces.white, faces.pale, faces.white, faces.pale, faces.side, faces.white])
    box(.64, .08, .34, 0, .53, z, faces.pale)
  }
  box(.65, .28, 1.66, 0, 1.87, 0, [faces.side, faces.pale, faces.blue, faces.pale, faces.white, faces.white])
  box(.33, .17, .025, 0, 1.88, .846, faces.gray)
  box(.23, .10, .012, 0, 1.88, .864, faces.pale)
  box(.10, .035, .012, .20, 1.88, .864, faces.green)
  const scanMaterial = material(palette.blue, { transparent: true, opacity: .12, side: THREE.DoubleSide, depthWrite: false })
  const scan = new THREE.Mesh(remember(new THREE.PlaneGeometry(.99, .79)), scanMaterial)
  scan.rotation.y = Math.PI / 2
  scan.position.set(0, 1.29, 0)
  root.add(scan)
  const cargo = Array.from({ length: cargoCount }, (_, index) => {
    const group = new THREE.Group()
    root.add(group)
    const surfaces = (index % 2 === 0 ? [faces.white, faces.pale, faces.white, faces.pale, faces.pale, faces.white] : [faces.side, faces.pale, faces.blue, faces.pale, faces.blue, faces.side]).map(original => {
      const copy = original.clone()
      materials.add(copy)
      return copy
    })
    const cargoEdge = edge.clone()
    materials.add(cargoEdge)
    const geometry = remember(new THREE.BoxGeometry(.50, .48, .54))
    group.add(new THREE.Mesh(geometry, surfaces))
    group.add(new THREE.LineSegments(remember(new THREE.EdgesGeometry(geometry)), cargoEdge))
    const badge = new THREE.Group()
    badge.name = 'inspection-badge'
    badge.position.set(.02, .47, .02)
    badge.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), viewDirection.clone().normalize())
    group.add(badge)
    const green = faces.green.clone(), white = faces.white.clone()
    materials.add(green); materials.add(white)
    badge.add(new THREE.Mesh(remember(new THREE.CircleGeometry(.145, 32)), green))
    const tick = new THREE.Shape()
    tick.moveTo(-.091, -.005); tick.lineTo(-.033, -.064); tick.lineTo(.095, .065)
    tick.lineTo(.070, .091); tick.lineTo(-.033, -.012); tick.lineTo(-.067, .022); tick.closePath()
    const mark = new THREE.Mesh(remember(new THREE.ShapeGeometry(tick)), white)
    mark.position.z = .003
    badge.add(mark)
    return { group, badge, surfaces: [...surfaces, cargoEdge, green, white], index }
  })
  // Invisible bounds preserve camera framing throughout the entire transport cycle.
  const bounds = new THREE.Mesh(remember(new THREE.BoxGeometry(5.7, 2.05, 2.8)), faces.ground)
  bounds.position.y = 1.01
  bounds.visible = false
  root.add(bounds)
  function update(elapsedSeconds) {
    const phase = ((elapsedSeconds % duration) + duration) % duration / duration
    // Belt and cargo share the same world speed; reset happens beyond the viewport.
    const beltPhase = phase * beltPerimeter / 2
    slats.forEach((slat, index) => {
      const point = beltPoint((index / slats.length * beltPerimeter + beltPhase) % beltPerimeter)
      slat.position.set(point.x, point.y, 0)
      slat.rotation.z = point.angle
    })
    const positions = [], passed = [], packages = []
    cargo.forEach(item => {
      const progress = (phase + item.index / cargo.length) % 1
      const x = routeStart + progress * (routeEnd - routeStart)
      const opacity = 1
      item.group.position.set(x, beltTop + .25, 0)
      item.group.visible = true
      const inspected = x > .60
      item.badge.visible = inspected
      const arrival = THREE.MathUtils.clamp((x - .60) / .064, 0, 1)
      item.badge.scale.setScalar(.72 + .28 * (1 - (1 - arrival) ** 3))
      positions.push(x); passed.push(inspected)
      packages.push({ x, opacity, visible: item.group.visible, checked: inspected, checkVisible: inspected && item.group.visible })
    })
    const scanPhase = phase * Math.PI * 2 * Math.round(duration * 2)
    scan.position.x = -.14 + .28 * (.5 + .5 * Math.sin(scanPhase))
    scanMaterial.opacity = .10 + .08 * (.5 + .5 * Math.sin(scanPhase))
    Object.assign(root.userData, { elapsed: elapsedSeconds, phase, cargoPositions: positions, passed, packages, beltPhase, duration, cycleSeconds: duration, routeStart, routeEnd, cargoSpeed, halfLength, slatCount, cargoCount })
    return root.userData
  }
  update(0)
  const cameraRotation = new THREE.Quaternion(), parentRotation = new THREE.Quaternion()
  function faceCamera(camera) {
    camera.getWorldQuaternion(cameraRotation)
    for (const { group, badge } of cargo) {
      group.getWorldQuaternion(parentRotation)
      badge.quaternion.copy(parentRotation).invert().multiply(cameraRotation)
    }
  }
  return { root, update, faceCamera, duration, cycleSeconds: duration, framingBounds, framingPoints, routeStart, routeEnd, cargoSpeed, halfLength, slatCount, getState: () => root.userData, dispose() { geometries.forEach(geometry => geometry.dispose()); materials.forEach(surface => surface.dispose()) } }
}
